import type { AuthUser } from '@/api/models';
import type { AuthMethod } from '@/utils/authSession';

/**
 * Who may see and change the operation catalog.
 *
 * This is the per-domain access module every accounting screen carries — the
 * shape of `accountsAccess` and `settingsAccess`, deliberately, so two surfaces
 * with the same policy do not grow two ways of expressing it. (The series brief
 * called for a single shared `accountingAccess.ts`; there is no such file, and
 * introducing one would mean rewriting five shipped screens in a task that ships
 * a sixth. Collapsing the five is worth doing and is not this task.)
 *
 * It mirrors the bos-api policy on `GET /ledger/operations`, which carries a
 * constraint that is easy to miss: it requires `auth_method=password` **as well
 * as** a role. A BranchAdmin holding a PIN-issued kiosk token is a BranchAdmin,
 * and still receives 403.
 *
 * | | policy | this client |
 * | --- | --- | --- |
 * | read the catalog | `BranchAdmin` = OrgAdmin\|BranchAdmin + password | `readOnly` / `full` |
 * | edit a template | `OrgAdmin` + password (task 02) | `full` |
 * | record an operation | `PosOperator` = OrgAdmin\|BranchAdmin\|Seller, any method (task 03) | `readOnly` / `full` |
 * | read the activity register | `BranchAdmin` (task 04) | `readOnly` / `full` |
 * | reverse a recorded operation | `OrgAdmin` — the **journal's** reverse policy (task 04) | `full` |
 *
 * **A note on Sellers, now answered.** The series brief says all three roles may
 * view the catalog. They cannot: the read endpoint is the `BranchAdmin` policy
 * family like every other ledger read, and a Seller never reaches the
 * back-office at all — the Accounting nav group is gated to OrgAdmin and
 * BranchAdmin. Granting a view here that the server refuses would produce a
 * screen that can only 403, which is the exact failure this module exists to
 * prevent.
 *
 * What a Seller genuinely needs is to *record* an operation, and task 03 settled
 * how: the three recording routes are the `PosOperator` policy — OrgAdmin,
 * BranchAdmin **or Seller**, with no `auth_method` constraint — while the
 * catalog and the editor stayed where they were. So the read was not widened and
 * the POS got no narrowed sibling; the recording routes are simply a different
 * policy on the same controller, and a Seller reaches them from bos-seller.
 * Nothing in bos-app does, which is why {@link canRecordOperations} is stricter
 * than the API.
 */
export type OperationsAccess =
    /**
     * A PIN/kiosk session. Every operations endpoint will 403 whatever the role,
     * so the page must not fetch at all — it explains that the catalog needs a
     * password sign-in.
     */
    | { kind: 'kiosk' }
    /** Signed in, but the role does not permit even reading (a Seller). */
    | { kind: 'forbidden' }
    /** BranchAdmin: the whole catalog is readable, nothing is editable. */
    | { kind: 'readOnly' }
    /** OrgAdmin with a password session: read now, edit from task 02. */
    | { kind: 'full' };

/**
 * Derive the access level from the persisted session.
 *
 * Fails closed on every unknown: no user, or an `auth_method` that could not be
 * read, is treated as a kiosk session rather than optimistically rendering a
 * page whose every request would 403.
 */
export const deriveOperationsAccess = (
    user: AuthUser | null,
    authMethod: AuthMethod | null,
): OperationsAccess => {
    if (!user) {
        return { kind: 'kiosk' };
    }
    if (authMethod !== 'password') {
        return { kind: 'kiosk' };
    }
    switch (user.role) {
        case 'OrgAdmin':
            return { kind: 'full' };
        case 'BranchAdmin':
            return { kind: 'readOnly' };
        default:
            return { kind: 'forbidden' };
    }
};

/** Whether this access level may read the catalog at all — and so, may fetch. */
export const canReadOperations = (access: OperationsAccess): boolean =>
    access.kind === 'full' || access.kind === 'readOnly';

/**
 * Whether this access level may edit a template — the editor, the publish, and
 * block/unblock.
 *
 * `full` only, mirroring the API: every mutation under `/ledger/operations` is
 * the `OrgAdmin` policy. A BranchAdmin reads a recipe and changes none of it,
 * which is what `readOnly` means on this screen: the editor opens, every control
 * is disabled, and there is no Save or Publish.
 */
export const canEditOperations = (access: OperationsAccess): boolean =>
    access.kind === 'full';

/**
 * Whether this session may **record** an operation — the Record tab's own gate,
 * and deliberately not `canEditOperations`.
 *
 * Recording is not editing. A BranchAdmin reads a recipe and cannot change it
 * (`readOnly`), and records against it every day: that is the ordinary case this
 * whole feature exists for. Deriving the Record tab from `canEditOperations`
 * would leave the operation to the one role least likely to be standing at the
 * till.
 *
 * **Which operations, though, is not this function's question.** Each template
 * carries its own `rolesAllowed`, the server filters
 * `GET /ledger/operations/recordable` by the caller's role, and it re-decides at
 * record time. So this answers "may this session ask?" and the picker's contents
 * answer "what may it record?" — which is why an empty picker is a data-driven
 * empty state and not a hidden tab.
 *
 * `full` and `readOnly` only, which is stricter than the API. The recording
 * routes are the `PosOperator` policy and put no `auth_method` constraint on a
 * PIN-issued token, so a kiosk BranchAdmin would be served — but nothing in
 * bos-app runs on a kiosk token: the Accounting nav is password-only and every
 * other endpoint this page needs would 403 anyway. The POS is where a PIN
 * session records, and that is bos-seller's screen to build, not this one.
 */
export const canRecordOperations = (access: OperationsAccess): boolean =>
    access.kind === 'full' || access.kind === 'readOnly';

/**
 * Whether this session may **reverse** a recorded operation.
 *
 * `full` only, and the policy it mirrors is the **journal's**, not this
 * resource's: `POST /ledger/journal-entries/{id}/reverse` is `OrgAdmin`, because
 * reversing a recorded operation is the ordinary reversal of the ordinary entry
 * it posted. That it coincides with {@link canEditOperations} today is arithmetic,
 * not meaning — one is "may rewrite a recipe", this is "may move the books" —
 * and they are kept apart so that a change to either does not silently move the
 * other.
 *
 * The Activity tab hides the Reverse action without this. It does **not**
 * pre-hide the action on an already-reversed entry: the server refuses a second
 * reversal, and a drawer's idea of what is still reversible can be a moment
 * behind another admin's.
 */
export const canReverseOperations = (access: OperationsAccess): boolean =>
    access.kind === 'full';

/** i18n keys (namespace `pages`) explaining why the page is restricted. */
export const OPERATIONS_ACCESS_COPY: Record<
    Exclude<OperationsAccess['kind'], 'full'>,
    { titleKey: string; bodyKey: string }
> = {
    kiosk: {
        titleKey: 'operations.access.kiosk.title',
        bodyKey: 'operations.access.kiosk.body',
    },
    forbidden: {
        titleKey: 'operations.access.forbidden.title',
        bodyKey: 'operations.access.forbidden.body',
    },
    readOnly: {
        titleKey: 'operations.access.readOnly.title',
        bodyKey: 'operations.access.readOnly.body',
    },
};
